import {
  createContext,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { cn } from '../../../lib/utils'
import {
  readStoredSuperadminTheme,
  writeStoredSuperadminTheme,
  type SuperadminTheme,
} from '../shared/constants/themeStorage'

export interface SuperadminThemeContextValue {
  theme: SuperadminTheme
  setTheme: (theme: SuperadminTheme) => void
  toggleTheme: () => void
}

export const SuperadminThemeContext = createContext<SuperadminThemeContextValue | null>(null)

export interface SuperadminThemeProviderProps {
  children: ReactNode
}

/**
 * Scoped theme voor Super Admin: `.superadmin-root` + optioneel `.dark`.
 * Slaat voorkeur op in localStorage (`sa-theme`). Beïnvloedt geen klant/backoffice UI.
 */
export function SuperadminThemeProvider ({ children }: SuperadminThemeProviderProps) {
  const [theme, setThemeState] = useState<SuperadminTheme>(readStoredSuperadminTheme)

  const setTheme = useCallback((next: SuperadminTheme) => {
    setThemeState(next)
    writeStoredSuperadminTheme(next)
  }, [])

  const toggleTheme = useCallback(() => {
    setThemeState((current) => {
      const next: SuperadminTheme = current === 'light' ? 'dark' : 'light'
      writeStoredSuperadminTheme(next)
      return next
    })
  }, [])

  const value = useMemo<SuperadminThemeContextValue>(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme]
  )

  return (
    <div
      className={cn(
        'superadmin-root min-h-dvh bg-sa-background text-sa-foreground',
        theme === 'dark' && 'dark'
      )}
      lang="nl"
      data-sa-theme={theme}
    >
      <SuperadminThemeContext.Provider value={value}>
        {children}
      </SuperadminThemeContext.Provider>
    </div>
  )
}
